import { CdkConnectedOverlay, CdkOverlayOrigin, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    input,
    Input,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {
    IGenericTypeAheadDropdownConfig,
    ITypeAheadDropdownAction
} from '../../interfaces/type-ahead-dropdown-config.interface';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { ObjectExtensionsService } from '../../services';
import { CDK_IMPORTS } from '../../shared/standalone-imports';
import { BadgeComponent } from '../badge/badge.component';
import { BaseFilterComponent } from '../filters/base-filter/base-filter.component';
import { OptionsMenuComponent } from '../options-menu/options-menu.component';
import { SearchInputComponent } from '../search-input/search-input.component';

@Component({
    selector: 'lib-generic-type-ahead-multiselect',
    templateUrl: './generic-type-ahead-multiselect.component.html',
    styleUrls: ['./generic-type-ahead-multiselect.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CommonModule, IdGeneratorPipe, ...CDK_IMPORTS, SearchInputComponent, OptionsMenuComponent, BadgeComponent]
})
export class GenericTypeAheadMultiselectComponent<T> extends BaseFilterComponent implements OnInit, OnChanges {
    @ViewChild(CdkOverlayOrigin, { static: true }) overlayOrigin!: CdkOverlayOrigin;

    @ViewChild('typeAheadContainer', { static: true }) typeAheadContainer!: ElementRef<HTMLDivElement>;

    @ViewChild('overlayPanel') overlayPanel?: CdkConnectedOverlay;

    @ViewChild('searchInput')
    searchInput?: SearchInputComponent;

    @Input() source?: T[];

    @Input() config?: IGenericTypeAheadDropdownConfig<T>;

    @Input() selectedItems?: T[];

    @Input() text = '';

    @Input() isReadOnly?: boolean;

    @Input() displaySelected? = true;

    @Input() noResultsText? = 'No matches found';

    @Input() name?: string;

    @Input() key?: keyof T;

    @Input() removeKey?: string;

    @Input() action?: ITypeAheadDropdownAction;

    @Input() allowToSaveNewItem?: boolean;

    @Input() showOptions = true;

    @Output() textChange: EventEmitter<string> = new EventEmitter<string>();

    @Output() itemClicked: EventEmitter<T> = new EventEmitter<T>();

    @Output() selectedItemsChange = new EventEmitter<T[]>();

    @Output() removeItemChange = new EventEmitter<T>();

    @Input() error?: boolean;

    @Input() genericTypeAheadMultiselectId?: string;

    placeholder = input<string | undefined>('Select an item');

    disabled = input<boolean>(false);

    opened = signal(false);

    filterActive = signal(false);
    sourceItems: T[] | undefined = [];
    focusList?: boolean;
    overlayWidth = signal<number>(0);
    override filterChanged = new EventEmitter<void>();
    protected scrollStrategy!: ScrollStrategy;

    private readonly objectExtensionsService = inject(ObjectExtensionsService);
    private readonly overlay = inject(Overlay);

    ngOnInit(): void {
        this.filterChanged = new EventEmitter();
        this.scrollStrategy = this.overlay.scrollStrategies.close();
        this.config = {
            ...this.config,
            multiple: this.config?.multiple ?? true
        } as IGenericTypeAheadDropdownConfig<T>;
        this.updateList();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['source']?.currentValue && !changes['source'].firstChange) {
            this.updateList();
        }
    }

    updateList(): void {
        if (!this.source) return;

        this.sourceItems = this.source.length > 0 ? this.removeSelectedItems(this.source) : [];
    }

    selectItem(item: T): void {
        if (!this.selectedItems) this.selectedItems = [];

        if (!this.isItemDuplicated(item, this.selectedItems)) {
            this.selectedItems.push(item);
            this.updateList();
            this.updateOverlayPosition();
            if (!this.sourceItems?.length) this.closePanel();
            this.getItemsAndEmitChanges();
        }
    }

    getTemplate(): TemplateRef<unknown> | null {
        if (!this.config?.itemTemplate) return null;
        return this.config.itemTemplate;
    }

    openOptionsMenu(event: Event): void {
        if (this.isReadOnly) return;

        this.filterActive.set(true);
        this.overlayWidth.set(this.typeAheadContainer.nativeElement.offsetWidth);

        if (event instanceof MouseEvent) {
            this.focusInput();
        }
    }
    async onTextChanged(text: string): Promise<void> {
        this.opened.set(true);
        const value = typeof text === 'string' ? text : '';
        if (value.length > 0) {
            this.overlayWidth.set(this.typeAheadContainer.nativeElement.offsetWidth);
            this.filterActive.set(true);
            this.textChange.emit(value);
        } else {
            this.sourceItems = [];
            this.textChange.emit(value);
        }
    }

    emitItemsChanged(items: T[]): void {
        this.selectedItemsChange.emit(items);
    }

    removeItem(item: T) {
        const objectIndex = this.selectedItems?.indexOf(item);
        if (objectIndex != undefined && objectIndex >= 0) this.selectedItems?.splice(objectIndex, 1);

        this.sourceItems = this.removeSelectedItems(this.source);
        this.removeItemChange.emit(item);
        this.getItemsAndEmitChanges();
    }

    onRunAction() {
        this.filterActive.set(false);
    }

    onMenuOpenChange(open: boolean): void {
        this.filterActive.set(open);
        if (!open) {
            this.text = '';
        }
    }

    focusInput() {
        if (this.filterActive()) {
            setTimeout(() => {
                this.searchInput?.inputElement?.nativeElement.focus();
                this.updateList();
                if (this.sourceItems && this.sourceItems.length > 0) {
                    this.opened.set(true);
                } else if (!this.text && this.config?.openOnFocus) {
                    this.textChange.emit(this.text);
                }
            }, 0);
        }
    }

    closePanel() {
        this.filterActive.set(false);
        this.focusList = false;
        this.opened.set(false);
        this.text = '';
        this.typeAheadContainer.nativeElement.focus();
    }

    onKeyDown(event: KeyboardEvent): void {
        if (this.sourceItems && this.sourceItems.length && this.filterActive() && this.opened()) {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Tab') {
                event.preventDefault();
                this.focusList = true;
            } else if (event.key === 'Escape') {
                this.closePanel();
            }
        }

        if (event.key === 'Enter' && this.allowToSaveNewItem && this.filterActive() && this.text.trim()) {
            this.text = this.text.trim();

            const item: any = { id: 0, name: this.text };

            if (this.text) {
                const found = this.source?.find((x: any) => x.name.toLowerCase() === this.text.toLowerCase());

                if (found) {
                    this.selectItem(found);
                } else if (!this.selectedItems?.some((x: any) => x.name.toLowerCase() === this.text.toLowerCase())) {
                    this.selectItem(item);
                }
            }

            this.closePanel();
        }
    }

    selectItemKeydownHandler({ event, item }: { event: KeyboardEvent; item: T }): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectItem(item);
        }
    }

    onBlurEvent(event?: FocusEvent): void {
        const relatedTarget = event?.relatedTarget as HTMLElement;
        if (relatedTarget && this.isElementInOptionsMenu(relatedTarget)) {
            return;
        }

        this.filterActive.set(false);
    }

    private isElementInOptionsMenu(element: HTMLElement): boolean {
        return element.closest('.optionsMenu') !== null || element.closest('lib-options-menu') !== null;
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.overlayWidth.set(this.typeAheadContainer.nativeElement.offsetWidth);
    }

    private isItemDuplicated(item: T, selectedItems: T[]): boolean {
        return selectedItems.some((selectedItem) =>
            this.objectExtensionsService.isEqualTo(selectedItem as object, item as object)
        );
    }

    private removeSelectedItems(source: T[] | undefined): T[] | undefined {
        if (!source) return;

        if (this.selectedItems?.length) {
            const sourceItems = source.filter(
                (sourceItem) =>
                    !this.selectedItems?.some(
                        (selectedItem) =>
                            (selectedItem as any)[this.removeKey ?? 'id'] ===
                            (sourceItem as any)[this.removeKey ?? 'id']
                    )
            );

            return sourceItems;
        } else {
            return source;
        }
    }

    private getItemsAndEmitChanges(): void {
        if (this.selectedItems) {
            this.emitItemsChanged(this.selectedItems);
        }
    }

    private updateOverlayPosition() {
        setTimeout(() => {
            if (!this.overlayPanel?.overlayRef) return;
            this.overlayPanel.overlayRef.updatePosition();
        }, 0);
    }
}
